import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useVehicle } from '../contexts/VehicleContext';
import { Navbar } from './Navbar';
import { SearchSection } from './SearchSection';
import { userService } from '../services/apiService';
import {
  getDisplayNameForUI,
  getProfileDisplayName,
  maskPhoneForDisplay,
  maskRegistration,
} from '../utils/displayName';
import { stripDuplicateMakeFromModel } from '../data/vehicleData';
import { toast } from 'react-toastify';
import {
  FaUser,
  FaPhone,
  FaPlus,
  FaTimes,
  FaChevronRight,
  FaShoppingBag,
  FaShieldAlt,
  FaHeart,
  FaMapMarkerAlt,
  FaWallet,
  FaBell,
  FaGift,
  FaHeadset,
  FaLock,
  FaCar,
  FaHeartbeat,
} from 'react-icons/fa';

const ACCOUNT_SECTIONS = [
  { label: 'Order History', href: '/myorder', icon: FaShoppingBag },
  { label: 'Car Health Passport', href: '/health-passport', icon: FaCar },
  { label: 'Warranties', href: '/warranty-cards', icon: FaShieldAlt },
  { label: 'Wishlist', href: '/account-settings?tab=wishlist', icon: FaHeart },
  { label: 'Saved Addresses', href: '/account-settings?tab=addresses', icon: FaMapMarkerAlt },
  { label: 'Wallet', href: '/account-settings?tab=wallet', icon: FaWallet },
  { label: 'Notifications', href: '/account-settings?tab=notifications', icon: FaBell },
  { label: 'Referral', href: '/account-settings?tab=referral', icon: FaGift },
  { label: 'Support', href: '/account-settings?tab=support', icon: FaHeadset },
  { label: 'Privacy', href: '/account-settings?tab=privacy', icon: FaLock },
];

export const MyProfile = () => {
  const { user, getProfile, updateUser } = useAuth();
  const {
    vehicles,
    selectedVehicle,
    loading: vehiclesLoading,
    selectVehicle,
    removeVehicle,
    fetchVehicles,
  } = useVehicle();
  const navigate = useNavigate();

  const [isEditingName, setIsEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [vehicleToDelete, setVehicleToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const displayName = getDisplayNameForUI(user);
  const maskedPhone = maskPhoneForDisplay(user?.phone);

  useEffect(() => {
    fetchVehicles();
    getProfile?.().catch(() => {});
  }, [fetchVehicles, getProfile]);

  useEffect(() => {
    if (!isEditingName) {
      setNameDraft(getProfileDisplayName(user) || displayName || '');
    }
  }, [user, displayName, isEditingName]);

  const startNameEdit = () => {
    setNameDraft(getProfileDisplayName(user) || displayName || '');
    setIsEditingName(true);
  };

  const cancelNameEdit = () => {
    setIsEditingName(false);
    setNameDraft(getProfileDisplayName(user) || displayName || '');
  };

  const saveDisplayName = useCallback(async () => {
    const trimmed = nameDraft.trim();
    if (!trimmed) {
      toast.error('Display name cannot be empty');
      return;
    }
    const userId = user?.id || user?._id;
    if (!userId) return;

    try {
      setSavingName(true);
      const result = await userService.updateUser(userId, { name: trimmed });
      const updated = result?.user || result?.data?.user || { ...user, name: trimmed };
      updateUser?.(updated);
      await getProfile?.();
      setIsEditingName(false);
      toast.success('Display name updated');
    } catch (err) {
      toast.error(err.message || 'Failed to update name');
    } finally {
      setSavingName(false);
    }
  }, [nameDraft, user, updateUser, getProfile]);

  const handleNameKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveDisplayName();
    } else if (e.key === 'Escape') {
      cancelNameEdit();
    }
  };

  const vehicleId = (v) => v?._id || v?.id;

  const isVehicleActive = (vehicle) => {
    const id = vehicleId(vehicle);
    const selectedId = vehicleId(selectedVehicle);
    return Boolean(vehicle.isDefault || (id && selectedId && String(id) === String(selectedId)));
  };

  const handleSetActive = async (vehicle) => {
    await selectVehicle(vehicle);
    toast.success('Active vehicle updated');
  };

  const handleFindParts = (vehicle) => {
    selectVehicle(vehicle);
    const params = new URLSearchParams({
      vehicleMake: vehicle.make,
      vehicleModel: vehicle.model,
      vehicleYear: String(vehicle.year || ''),
      vehicleVariant: vehicle.variant || '',
      spareType: 'new',
      origin: 'OEM',
    });
    navigate(`/catalog?${params.toString()}`);
  };

  const handleViewPassport = (vehicle) => {
    const id = vehicleId(vehicle);
    navigate(id ? `/health-passport?vehicleId=${id}` : '/health-passport');
  };

  const confirmDelete = async () => {
    const id = vehicleId(vehicleToDelete);
    if (!id) return;
    try {
      setDeleting(true);
      await removeVehicle(id);
      toast.success('Vehicle deleted');
      setVehicleToDelete(null);
    } catch {
      toast.error('Failed to delete vehicle');
    } finally {
      setDeleting(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center text-gray-600">Please log in to view your profile</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      <div className="max-w-7xl mx-auto px-4 py-8 relative z-[1]">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-4 md:mb-0">
            My <span className="text-red-500">Account</span>
          </h1>
          <Navbar />
        </div>

        {/* Profile section — B-10A */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
            <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <FaUser className="text-red-500" /> Profile
            </h2>
            {!isEditingName && (
              <button
                type="button"
                onClick={startNameEdit}
                className="text-sm font-medium text-blue-600 hover:text-blue-700"
              >
                Edit
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <p className="text-sm text-gray-500 mb-1">Display name</p>
              {isEditingName ? (
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="text"
                    value={nameDraft}
                    onChange={(e) => setNameDraft(e.target.value)}
                    onKeyDown={handleNameKeyDown}
                    autoFocus
                    disabled={savingName}
                    className="flex-1 min-w-[180px] px-3 py-2 border border-blue-400 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none font-medium text-gray-800"
                    aria-label="Display name"
                  />
                  <button
                    type="button"
                    onClick={saveDisplayName}
                    disabled={savingName}
                    className="text-sm font-medium text-blue-600 hover:text-blue-700 disabled:opacity-50"
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={cancelNameEdit}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <p className="font-medium text-gray-800 text-lg">{displayName || '—'}</p>
              )}
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Phone</p>
              <p className="font-medium text-gray-800 flex items-center gap-2">
                <FaPhone className="text-gray-400 shrink-0" />
                {maskedPhone}
              </p>
            </div>
          </div>
        </div>

        {/* Saved vehicles */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2 mb-4">
            <FaCar className="text-red-500" /> Saved vehicles
          </h2>

          {vehiclesLoading && vehicles.length === 0 ? (
            <p className="text-gray-500 text-sm">Loading vehicles…</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {vehicles.map((vehicle) => {
                const active = isVehicleActive(vehicle);
                const modelLabel = stripDuplicateMakeFromModel(vehicle.make, vehicle.model);
                const maskedReg = maskRegistration(vehicle.registrationNumber);

                const cardTitle =
                  vehicle.nickname?.trim() ||
                  `${vehicle.make || ''} ${modelLabel}`.trim() ||
                  'Your vehicle';

                return (
                  <div
                    key={vehicleId(vehicle)}
                    className={`rounded-xl border overflow-hidden flex flex-col shadow-sm ${
                      active ? 'border-blue-500 ring-1 ring-blue-200' : 'border-gray-200'
                    }`}
                  >
                    <div className={`px-4 py-3 ${active ? 'bg-blue-600' : 'bg-slate-800'}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <h3 className="font-semibold text-white truncate">{cardTitle}</h3>
                          <p className="text-xs text-slate-300 mt-0.5">
                            {vehicle.year}
                            {vehicle.variant ? ` · ${vehicle.variant}` : ''}
                          </p>
                        </div>
                        {active && (
                          <span className="shrink-0 px-2 py-0.5 bg-white/20 text-white text-[10px] font-bold uppercase rounded-full">
                            Active
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="p-4 flex flex-col flex-1 bg-white">
                      {maskedReg && (
                        <p className="text-xs font-mono text-gray-500 mb-2">{maskedReg}</p>
                      )}
                      {vehicle.fuelType && (
                        <span className="inline-flex self-start px-2 py-0.5 mb-3 text-[10px] font-semibold uppercase rounded-full bg-gray-100 text-gray-600">
                          {vehicle.fuelType}
                        </span>
                      )}

                      <div className="mt-auto space-y-3">
                        <button
                          type="button"
                          onClick={() => handleViewPassport(vehicle)}
                          className="w-full inline-flex items-center justify-center gap-2 text-sm font-semibold px-3 py-2.5 rounded-xl bg-slate-800 text-white hover:bg-slate-700 transition-colors"
                        >
                          <FaHeartbeat className="text-xs" />
                          Health Passport
                        </button>
                        <div className="grid grid-cols-2 gap-2">
                          {!active ? (
                            <button
                              type="button"
                              onClick={() => handleSetActive(vehicle)}
                              className="text-xs font-medium px-2 py-2 rounded-lg border border-blue-200 text-blue-700 hover:bg-blue-50"
                            >
                              Set Active
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleFindParts(vehicle)}
                              className="col-span-2 text-xs font-medium px-2 py-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50"
                            >
                              Find Parts
                            </button>
                          )}
                          {!active && (
                            <button
                              type="button"
                              onClick={() => handleFindParts(vehicle)}
                              className="text-xs font-medium px-2 py-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50"
                            >
                              Parts
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => setVehicleToDelete(vehicle)}
                            className={`text-xs font-medium px-2 py-2 rounded-lg border border-red-100 text-red-600 hover:bg-red-50 ${
                              active ? 'col-span-2' : ''
                            }`}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              <button
                type="button"
                onClick={() => setShowAddModal(true)}
                className="rounded-xl border-2 border-dashed border-gray-300 hover:border-blue-400 hover:bg-blue-50/30 min-h-[160px] flex flex-col items-center justify-center gap-2 text-gray-600 hover:text-blue-600 transition-colors"
              >
                <FaPlus className="text-2xl" />
                <span className="text-sm font-semibold">Add Vehicle</span>
              </button>
            </div>
          )}
        </div>

        {/* Account sections */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Account</h2>
          <ul className="divide-y divide-gray-100">
            {ACCOUNT_SECTIONS.map((section) => {
              const Icon = section.icon;
              return (
                <li key={section.label}>
                  <Link
                    to={section.href}
                    className="flex items-center justify-between py-3.5 px-1 text-gray-800 hover:text-blue-600 transition-colors group"
                  >
                    <span className="flex items-center gap-3">
                      <Icon className="text-gray-400 group-hover:text-blue-500" />
                      <span className="font-medium">{section.label}</span>
                    </span>
                    <FaChevronRight className="text-gray-300 text-sm" />
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      {showAddModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg shadow-2xl relative max-h-[90vh] overflow-y-auto">
            <button
              type="button"
              className="absolute top-3 right-3 text-gray-600 hover:text-black"
              onClick={() => setShowAddModal(false)}
              aria-label="Close"
            >
              <FaTimes />
            </button>
            <SearchSection
              embedded
              onClose={() => {
                setShowAddModal(false);
                fetchVehicles();
              }}
            />
          </div>
        </div>
      )}

      {vehicleToDelete && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl p-8 w-full max-w-md shadow-2xl relative">
            <button
              type="button"
              onClick={() => setVehicleToDelete(null)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-800"
              aria-label="Close"
            >
              <FaTimes />
            </button>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Delete vehicle?</h2>
            <p className="text-gray-700 font-medium mb-1">
              {vehicleToDelete.make} {vehicleToDelete.model} {vehicleToDelete.year}
            </p>
            <p className="text-sm text-gray-600 mb-6">
              This cannot be undone. The vehicle will be permanently removed from your account.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setVehicleToDelete(null)}
                className="flex-1 py-3 rounded-lg border border-gray-300 font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={deleting}
                className="flex-1 py-3 rounded-lg bg-red-600 text-white font-semibold hover:bg-red-700 disabled:opacity-60"
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
